/**
 * Pane 2 — Configure. Acts on the current photo selection: size, paper, qty,
 * frame (stocked configs dropdown), border and crop → Add to cart.
 *
 * Round-3 preview block: the live PrintPreview sits on a CHARCOAL surface so
 * white borders actually read, with ‹ › arrows (buttons + keyboard) to flip
 * through the selected photos, the ADJUST CROP button directly beneath it and
 * a plain-language auto-crop warning when the previewed photo doesn't match
 * the size. A gold "Suggested" chip proposes a sensible size (good DPI,
 * aspect-close, popular) with one-tap apply. The "Frame" dropdown lists EVERY
 * stocked frame config for the paper (any-size upsell, owner 2026-07-25);
 * picking one whose print size differs warns near the Add button and Add
 * creates the framed print at the FRAME'S print size. Print Check runs
 * synchronously as prints are created; anything that auto-crops surfaces
 * through the shared Review banner + modal. After Add the pane clears back to
 * a placeholder.
 */

import {
  framedProduct,
  parseSizeId,
  resolveBorder,
  type BorderMode,
  type FramedProduct,
  type PaperId,
  type PricingData,
  type ResolvedSize,
} from '@rpl/pricing';
import { useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { formatMoney } from '../../lib/format';
import { asRotation } from '../../lib/rotate';
import {
  borderMeasure,
  borderModeLabel,
  buildPrintFields,
  CUSTOM_SIZE_OPTION_KEY,
  defaultCtPaper,
  effectiveBorderFor,
  findLooseMergeTarget,
  flaggedForReview,
  defaultFrameColour,
  frameColourOptions,
  frameConfigOptionFor,
  frameConfigOptions,
  framedPaperSiblings,
  frameInColour,
  framePaperLabel,
  hasCustomCrop,
  isCtPaper,
  isPaperValidForSize,
  paperForSize,
  paperPickerGroups,
  paperRestrictionNote,
  previewCropWarning,
  priceForSafe,
  shortPaperLabel,
  sizeIdShort,
  sizePickerGroups,
  sizeShort, sizeWithUnit,
  suggestSize,
  type FrameConfigOption,
} from '../../lib/sizePaper';
import type { LocalPhoto, UiPrint } from '../../lib/types';
import { useStore } from '../../store/store';
import { FramePreviewModes } from '../cart/frame/FramePreviewModes';
import { CropEditor } from '../crop/CropEditor';
import { BorderControl } from './BorderControl';
import {
  cfgCanAdvance,
  cfgCtaLabel,
  cfgIsLast,
  cfgNext,
  cfgPrev,
  cfgProgress,
  cfgResolve,
  cfgSteps,
  type CfgStep,
} from './cfgSteps';
import { CustomSizeDialog } from './CustomSizeDialog';
import { Dropdown } from './Dropdown';
import { PrintPreview } from './PrintPreview';
import { QtyStepper } from './QtyStepper';
import { FrameColourPicker } from './FrameColourPicker';
import { reportUpsell } from '../../lib/upsellReport';
import {
  ANCHOR_BAND,
  anchorDefaultSize,
  cheapestFineArtDelta,
  largestPrintable,
  premiumPaperUpsell,
  priceDeltaLabel,
  upsellTarget,
} from '../../lib/upsell';
import { Segmented } from './Segmented';
import type { OrderActions } from './useOrderActions';

export interface AddResult {
  count: number;
  flagged: number;
  framed: boolean;
  /** Set when the add created prints for exactly ONE photo — the only case
   *  the post-Add frame nudge fires for (a multi-photo add would make
   *  "Frame this print?" ambiguous). */
  singlePrintId?: string;
}

/** Live state of the pane's PRIMARY action, mirrored by the sticky bar (round
 *  5). On mobile that action is the current step's CTA, not always Add. */
export interface AddMeta {
  canAdd: boolean;
  label: string;
  /** Render the bar's button gold (the real Add, not a "Next"). */
  gold?: boolean;
}

export interface ConfigurePaneProps {
  pricing: PricingData;
  catalog: Map<string, ResolvedSize>;
  actions: OrderActions;
  onOpenCrop: (printId: string) => void;
  /** Fired after a successful Add — drives the parent toast + Order-pane pulse. */
  onAdded: (result: AddResult) => void;
  /** Registers the Add action's live enabled-state + label with the parent. */
  onAddMeta?: (meta: AddMeta | null) => void;
  /** The parent's handle onto the pane's Add action (sticky-bar click). */
  addRef?: MutableRefObject<(() => void) | null>;
  /** Toast line (staged-adjustments feedback). */
  onToast?: (msg: string) => void;
}

const NO_FRAME = 'none';

export function ConfigurePane({
  pricing,
  catalog,
  actions,
  onOpenCrop,
  onAdded,
  onAddMeta,
  addRef,
  onToast,
}: ConfigurePaneProps) {
  const photos = useStore((s) => s.photos);
  const prints = useStore((s) => s.prints);
  const selection = useStore((s) => s.selection);
  const clearSelection = useStore((s) => s.clearSelection);
  const applyBar = useStore((s) => s.applyBar);
  const setApplyBar = useStore((s) => s.setApplyBar);
  const addCustomSize = useStore((s) => s.addCustomSize);
  const setStep = useStore((s) => s.setStep);
  // Pre-Add adjustments staged by the ✎ dialog (owner 2026-07-27).
  const stagedAdjust = useStore((s) => s.stagedAdjust);
  const setStagedAdjust = useStore((s) => s.setStagedAdjust);
  const isMobile = useIsMobile();

  const [customOpen, setCustomOpen] = useState(false);
  /** ✎ on a not-yet-added config: a DRAFT print for the crop dialog. */
  const [draft, setDraft] = useState<{ print: UiPrint; photo: LocalPhoto } | null>(null);
  // Store-held (survives desktop<->mobile remounts); dialog rendered by SizePaperScreen.
  const setPaperInfoOpen = useStore((s) => s.setPaperInfoOpen);

  const sizeId = applyBar.sizeId;
  const entry = sizeId ? catalog.get(sizeId) : undefined;
  const isFineArt = sizeId ? parseSizeId(sizeId).family === 'fine_art' : false;
  const paper = applyBar.paper;
  const price = sizeId ? priceForSafe(pricing, sizeId, paper) : null;

  const selectedPhotos = useMemo(
    () => photos.filter((p) => selection.has(p.id) && p.status === 'ready'),
    [photos, selection],
  );
  const count = selectedPhotos.length;

  /* ---------- preview pager (‹ › through the selection) ---------- */
  const [previewIdx, setPreviewIdx] = useState(0);
  const idx = count > 0 ? Math.min(previewIdx, count - 1) : 0;
  const previewPhoto = count > 0 ? selectedPhotos[idx] : undefined;
  const stepPreview = (delta: -1 | 1) => {
    if (count < 2) return;
    setPreviewIdx((idx + delta + count) % count);
  };

  /* ---------- stocked frames for this paper ---------- */
  // Any-size upsell (owner 2026-07-25): EVERY stocked frame for the paper is
  // offered regardless of the current print size — a mismatched pick just
  // warns near Add and the framed print is created at the FRAME'S size.
  /* ---------- frame COLOUR (owner 2026-07-28) ----------
     Its own control rather than doubling the frame list: the option labels are
     already long, and colour is orthogonal to which config you want. Store-held
     for the same reason as spPendingFrameId — MobileTabs unmounts this pane. */
  const colours = useMemo(() => frameColourOptions(pricing), [pricing]);
  const rawColour = useStore((s) => s.spFrameColour);
  const setFrameColour = useStore((s) => s.setSpFrameColour);
  const frameColour =
    colours.some((c) => c.id === rawColour) ? rawColour : defaultFrameColour(pricing);
  const frameOptions = useMemo(
    () => frameConfigOptions(pricing, catalog, paper, frameColour),
    [pricing, catalog, paper, frameColour],
  );
  // Store-held (owner 2026-07-27): a mobile tab switch unmounts this pane —
  // local state silently dropped the picked frame, so "Add framed" became a
  // LOOSE add that bumped the wrong cart line.
  const pendingFrameId = useStore((s) => s.spPendingFrameId);
  const setPendingFrameId = useStore((s) => s.setSpPendingFrameId);
  const pendingFrame = useMemo(
    () =>
      pendingFrameId
        ? frameOptions.find((o) => o.frameId === pendingFrameId) ??
          // A paper SIBLING picked via the frame-paper picker below — not the
          // upsell list's winner for its config, but a real framed product.
          frameConfigOptionFor(pricing, pendingFrameId, paper)
        : null,
    [frameOptions, pendingFrameId, pricing, paper],
  );
  /** Papers this frame config comes in — the paper choice while framed. */
  const frameSiblings = useMemo(
    () => (pendingFrame ? framedPaperSiblings(pricing, pendingFrame.frameId) : []),
    [pricing, pendingFrame],
  );
  // A paper flip re-chooses each config group's winner (fr-GL-5x7 → fr-LU-5x7),
  // so RE-MAP the pick to the same physical config on the new paper instead of
  // silently dropping the customer's frame; clear only when the config
  // genuinely vanished from the list.
  const lastFrameRef = useRef<FrameConfigOption | null>(null);
  useEffect(() => {
    if (pendingFrame) lastFrameRef.current = pendingFrame;
  }, [pendingFrame]);
  useEffect(() => {
    if (!pendingFrameId || pendingFrame) return;
    const prev = lastFrameRef.current;
    // Matched on the config INCLUDING colour, so a pricing refresh can never
    // hand the customer back a different colour than the one they picked.
    const same = prev
      ? frameOptions.find(
          (o) =>
            o.printSize === prev.printSize &&
            o.frameSize === prev.frameSize &&
            o.borderLabel === prev.borderLabel &&
            o.colour === prev.colour,
        )
      : undefined;
    setPendingFrameId(same ? same.frameId : null);
  }, [pendingFrameId, pendingFrame, frameOptions]);
  const framed = !!pendingFrame;
  /** The pane's DISPLAY size ("8x10") — sizeIds are family-prefixed, so a
   *  cross-family stand-in (ct-8x10 → fa-8x10) is NOT a size change. */
  const paneSize = useMemo(() => {
    try {
      return sizeId ? parseSizeId(sizeId).size : null;
    } catch {
      return null;
    }
  }, [sizeId]);
  /** The chosen frame changes the print size → warn near the Add button. */
  const frameSizeChanges = !!pendingFrame && !!paneSize && pendingFrame.printSize !== paneSize;
  /** The chosen frame prints on a different paper (cross-paper stand-in) → warn too. */
  const framePaperChanges = !!pendingFrame && pendingFrame.paper !== paper;

  /* ---------- border choice (store-held until Add) ----------
     Store-held (owner 2026-07-27): MobileTabs unmounts this pane on every tab
     switch, and losing a fine-art border mid-flow would silently create
     UNDECIDED prints — re-opening the border gate the mobile Border step
     exists to prevent. */
  const storedBorder = useStore((s) => s.spBorder);
  /* Clamped at RENDER time to what this size offers. The reset effect below
     runs after the first paint at a new size, and that one paint used to pair
     a fine-art 'handling' choice with a C-Type size, so PrintPreview's
     resolveBorder threw "Handling border is fine-art only" and took down
     Size & Paper (setApplyBar now clamps the store too; this is the guard). */
  const border: BorderMode | null =
    storedBorder != null && sizeId ? effectiveBorderFor(sizeId, storedBorder) : storedBorder;
  const setBorder = useStore((s) => s.setSpBorder);
  /* Reset only on a REAL size/family change. Review round 2026-07-27: this
     effect had no mount guard, so every remount re-ran it — and since
     MobileTabs remounts the pane on every tab switch, a peek at the Cart wiped
     the border the customer had just chosen, defeating the whole point of
     holding it in the store. null means "not chosen yet"; C-Type has no such
     state, so a null (doAdd clears it after each batch) is seeded to 'none'. */
  const borderKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const key = `${isFineArt}|${sizeId ?? ''}`;
    const prev = borderKeyRef.current;
    borderKeyRef.current = key;
    if (prev !== null && prev !== key) setBorder(isFineArt ? null : 'none');
    else if (!isFineArt && useStore.getState().spBorder === null) setBorder('none');
  }, [isFineArt, sizeId, setBorder]);

  const sizeGroups = useMemo(
    () => sizePickerGroups(pricing, catalog, paper, {
        customAction: true,
        currentSizeId: sizeId,
        fitPhoto: previewPhoto,
      }),
    [pricing, catalog, paper, sizeId, previewPhoto],
  );
  const paperGroups = useMemo(
    () => (isFineArt && sizeId ? paperPickerGroups(pricing, sizeId, paper) : []),
    [pricing, isFineArt, sizeId, paper],
  );
  const ctPapers = pricing.productLines.ctype_standard.papers;

  /** CSS-mock framed product for the live preview when a frame is picked. */
  const framedMock = useMemo<FramedProduct | null>(() => {
    if (!pendingFrame) return null;
    try {
      return framedProduct(pricing, pendingFrame.frameId);
    } catch {
      return null;
    }
  }, [pricing, pendingFrame]);

  /* ---------- suggested size (previewed photo drives multi-selects) ---------- */
  // The chip skips sizes the chosen finish can't print (e.g. 8×12 on Gloss).

  /* PERMISSION CHIP (owner 2026-08-15, #7): WhiteWall's trust move — tell them
     what their file genuinely supports. Quality-gated by the same checkPrint
     rules as the suggestion, so it can never name a size that prints soft, and
     only speaks when that size is BIGGER than what they have picked. Tapping
     opens the size picker there; it never auto-applies a $40 size. */
  const [sizeOpenToken, setSizeOpenToken] = useState(0);
  const [upsellSeen, setUpsellSeen] = useState(false);
  /* Accepted upsells this session. After TWO the button retires: working the
     customer up the size list one rung at a time is nagging, not selling. */
  const [upsellTaken, setUpsellTaken] = useState(0);
  /* The target FROZEN while the size list is open. Without this, clicking
     "Prints up to 12×18"" flipped the button into fine-art mode on the same
     render, so the list opened highlighting 8×10 "Gallery quality" instead of
     the 12×18 the customer had just asked to see. */
  const [frozen, setFrozen] = useState<{ sizeId: string; badge: string } | null>(null);
  /* ONE adaptive upsell button beside the size picker (owner 2026-08-15
     round 4): it opens with "Prints up to 20×30"", and once they have chosen
     it keeps re-aiming — a C-Type pick turns it into the fine-art upgrade,
     a fine-art pick into the next size up. Never auto-applies. */
  const upsellRaw = useMemo(
    () =>
      previewPhoto && !framed
        ? upsellTarget(pricing, catalog, previewPhoto, actions.thresholds, sizeId, paper, upsellSeen)
        : null,
    [previewPhoto, framed, pricing, catalog, actions.thresholds, sizeId, paper, upsellSeen],
  );
  // Two accepted upsells is the cap (see upsellTaken).
  const upsell = upsellTaken >= 2 ? null : upsellRaw;
  /* The "Largest for this photo" row marker is a FACT about the photo, so it
     never disappears — unlike the button, which retires after two upsells. */
  const largestRow = useMemo(
    () => (previewPhoto ? largestPrintable(catalog, previewPhoto, actions.thresholds) : null),
    [previewPhoto, catalog, actions.thresholds],
  );

  /* FINE ART TIER CHIP (owner 2026-08-15, #5): only when fine art stocks the
     SAME dimensions — 4×6 has no equivalent, so its clean two-finish toggle is
     left alone. Priced as the cheapest upgrade delta ("from +$4.90"). */
  const fineArtUpgrade = useMemo(
    () => (!framed && sizeId && !isFineArt ? cheapestFineArtDelta(pricing, catalog, sizeId, paper) : null),
    [framed, sizeId, isFineArt, pricing, catalog, paper],
  );

  /* PREMIUM PAPER BUTTON (owner 2026-08-16, round 2 #2): on an entry-tier
     fine art paper, offer the premium twin (matt→Cotton Rag Smooth,
     lustre→Ilford Pearl) as a one-press delta apply. Shows ONLY while the
     size upsell button is absent — one green button at a time — and retires
     for good after one accept (no nagging if they switch back). */
  const [paperUpTaken, setPaperUpTaken] = useState(false);
  const paperUpsell = useMemo(
    () =>
      !framed && isFineArt && sizeId && !paperUpTaken && !upsell
        ? premiumPaperUpsell(pricing, sizeId, paper)
        : null,
    [framed, isFineArt, sizeId, paperUpTaken, upsell, pricing, paper],
  );

  /* ANCHOR DEFAULT (owner 2026-08-17, "option A"): on the FIRST photo a new
     order configures, replace the flat ct-4x6 bootstrap default with the
     ratio-fitting size nearest the $6-8 band (8×10/8×12), quality-gated.
     Guards: never after a human picked a size (sizeTouched), once per order
     (anchorSizeId), and never for BULK selections (>6 photos — McDonald's
     anchors a meal, not catering; big batches stay 4×6). */
  useEffect(() => {
    const st = useStore.getState();
    if (framed || !previewPhoto || st.sizeTouched || st.anchorSizeId) return;
    if (count === 0 || count > 6) return;
    const anchor = anchorDefaultSize(catalog, previewPhoto, actions.thresholds);
    if (!anchor) return;
    st.applyAnchorSize(anchor, paperForSize(pricing, anchor, paper));
    // Only a RAISED anchor is a merchandising event worth counting.
    if (ANCHOR_BAND.has(anchor)) reportUpsell('shown', 'anchor_default', anchor);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire per previewed photo; the store guards make it once
  }, [previewPhoto?.id, count, framed]);

  /* Instrumentation (#12): one `shown` per surface per context, so acceptance
     rate per surface is readable in two weeks and anything under ~3% can be
     killed on evidence rather than taste. */
  const shownRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!upsell) return;
    const key = `up:${upsell.kind}:${upsell.entry.sizeId}`;
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    reportUpsell('shown', `size_${upsell.kind}`, upsell.entry.sizeId);
  }, [upsell]);
  useEffect(() => {
    if (!fineArtUpgrade) return;
    const key = `fa:${fineArtUpgrade.faSizeId}`;
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    reportUpsell('shown', 'paper_fineart', fineArtUpgrade.faSizeId);
  }, [fineArtUpgrade]);
  useEffect(() => {
    if (!paperUpsell) return;
    const key = `pp:${paperUpsell.paper}`;
    if (shownRef.current.has(key)) return;
    shownRef.current.add(key);
    reportUpsell('shown', 'paper_premium', paperUpsell.paper);
  }, [paperUpsell]);

  /** "8×12 prints on Lustre only" — shown when the size restricts its finishes. */
  const restrictionNote = useMemo(
    () => (sizeId ? paperRestrictionNote(pricing, sizeId) : null),
    [pricing, sizeId],
  );

  /** The size the prints will ACTUALLY be created at — a picked frame wins
   *  (drives the pre-add mismatch warning under the preview). */
  const precheckEntry = pendingFrame ? catalog.get(pendingFrame.sizeId) ?? entry : entry;

  const canAdd = count > 0 && !!sizeId;
  /** Framed picks crop too (owner 2026-07-27) — via their framed print. */
  const canCrop = !!previewPhoto && (framed ? !!pendingFrame : !!sizeId);

  /* ---------- "no size yet" is a PROMPT, not a dead end (Clarity round 3) ----
     Below the count===0 early-return `count > 0` always holds, so a missing
     size is the ONLY thing that blocked Add. It used to render the gold button
     `disabled`, which is the classic dead-click generator: 127 dead clicks in
     3 days across the desktop Add and the mobile bottom bar, because a disabled
     button gives no reason and no next move. Now the button stays live, says
     what it wants, and the click DOES something — it takes them to the size
     picker and opens it. */
  const needsSize = !sizeId;
  const sizeFieldRef = useRef<HTMLDivElement>(null);
  const [sizeNudge, setSizeNudge] = useState(false);
  const nudgeTimer = useRef<number | null>(null);

  const openSizePicker = () => {
    const el = sizeFieldRef.current;
    if (!el) return;
    // Instant scroll, not smooth: the popover is position:fixed at the trigger
    // rect and closes on scroll, so a smooth scroll would shut it immediately.
    el.scrollIntoView({ block: 'center' });
    setSizeNudge(true);
    if (nudgeTimer.current) window.clearTimeout(nudgeTimer.current);
    nudgeTimer.current = window.setTimeout(() => setSizeNudge(false), 1400);
    // Next frame, so the scroll has settled before the popover measures itself.
    requestAnimationFrame(() => {
      el.querySelector<HTMLButtonElement>('.dd__btn')?.click();
    });
  };

  /** Send them to the size decision wherever it lives (mobile puts it on its
   *  own step, so jump there first and open the picker after that render). */
  const nudgeSize = () => {
    if (stepped && step !== 'size') {
      setCfgStep('size');
      requestAnimationFrame(() => openSizePicker());
      return;
    }
    openSizePicker();
  };

  useEffect(
    () => () => {
      if (nudgeTimer.current) window.clearTimeout(nudgeTimer.current);
    },
    [],
  );

  /* ---------- mobile stepped flow (owner 2026-07-27) ----------
     Phones show ONE decision at a time so the pane never scrolls; desktop
     keeps the full column. The blocks below are the SAME JSX either way —
     only wrapped in `stepOn(...)` guards. */
  const stepped = isMobile && count > 0;
  const steps = useMemo(() => cfgSteps({ framed, fineArt: isFineArt }), [framed, isFineArt]);
  const rawStep = useStore((s) => s.spCfgStep);
  const setCfgStep = useStore((s) => s.setSpCfgStep);
  const step = cfgResolve(steps, rawStep);
  const stepIdx = steps.indexOf(step);
  /** The final step carries Add to cart + the quantity stepper. */
  const isLastStep = cfgIsLast(steps, step);
  /** Show a block? Always on desktop; only on its own step when stepped. */
  const stepOn = (...keys: CfgStep[]) => !stepped || keys.includes(step);

  /* Which way the step panel should travel (owner 2026-07-27): without the
     movement the next step simply replaced the last one in place, so nothing
     said the screen had changed and you had to re-read the pane to find
     yourself. Forward slides in from the right, Back and chip jumps backwards
     slide in from the left. Adjusting state during render is React's own
     "derive from changing props" pattern — it re-renders before the commit,
     so the panel never paints with the wrong direction. */
  const [dirFrom, setDirFrom] = useState<CfgStep>(step);
  const [stepDir, setStepDir] = useState<'fwd' | 'back'>('fwd');
  if (dirFrom !== step) {
    setStepDir(steps.indexOf(step) < steps.indexOf(dirFrom) ? 'back' : 'fwd');
    setDirFrom(step);
  }

  /**
   * The print that "Add" merges into / "Adjustments" edits for the previewed
   * photo (photo × size × paper × border). Round-7 fix: once that print
   * carries an explicit crop or Fit choice (e.g. a custom crop saved in the
   * editor), the preview renders THAT geometry and the auto-crop warning
   * clears — the warning keys off the flaggedForReview resolution rule, not
   * the raw ratio mismatch.
   */
  // Colour target first (what Add merges into); else the B&W sibling made in
  // the editor — round-11 fix: a B&W print must PREVIEW as B&W here, not fall
  // back to a plain colour render because the colour-keyed lookup missed it.
  const previewPrint = useMemo(
    () =>
      previewPhoto && sizeId && !framed
        ? findLooseMergeTarget(prints, previewPhoto.id, sizeId, paper, border) ??
          findLooseMergeTarget(prints, previewPhoto.id, sizeId, paper, border, true)
        : undefined,
    [prints, previewPhoto, sizeId, paper, border, framed],
  );
  /** Staged pre-Add adjustments for the previewed photo (✎ before Add). */
  const stagedA = previewPhoto ? stagedAdjust[previewPhoto.id] : undefined;

  /** White-border width note — rendered UNDER the border buttons (owner
   *  2026-08-01: under the preview it stacked with the crop note and forced a
   *  scroll; here it is contextual and costs nothing when border is off).
   *  Honours a saved/staged custom width over the pricing default. */
  const wbCaption = useMemo(() => {
    if (framed || border !== 'white' || !sizeId) return null;
    try {
      const customMm = stagedA?.borderMm ?? previewPrint?.borderMm ?? null;
      const mm = customMm ?? resolveBorder(pricing, sizeId, 'white').mm;
      return `White border: ${borderMeasure(mm)}. Your photo prints smaller inside the sheet.`;
    } catch {
      return null;
    }
  }, [framed, border, sizeId, pricing, stagedA, previewPrint]);

  /**
   * The framed print ✎ would edit — the same lookup doCrop uses, and the print
   * whose crop decision RESOLVES the mismatch warning. Owner-reported bug
   * 2026-07-27: this passed `undefined` for framed, so the warning keyed off
   * the raw ratio alone and never cleared no matter how the crop was fixed.
   * Undefined (no framed print yet) still warns on ratio, which is the point
   * of the pre-Add warning.
   */
  const framedTarget = useMemo(
    () =>
      previewPhoto && pendingFrame
        ? prints.find(
            (p) =>
              p.photoId === previewPhoto.id &&
              p.frameId === pendingFrame.frameId &&
              !(p.bw ?? false),
          )
        : undefined,
    [prints, previewPhoto, pendingFrame],
  );
  // Framed too (owner 2026-07-27): checked against the FRAME'S print size —
  // frames add with a silent auto-crop, so the mismatch must warn pre-Add.
  const previewCrops =
    !stagedA &&
    !!previewPhoto &&
    !!precheckEntry &&
    previewCropWarning(
      previewPhoto,
      precheckEntry.inches,
      framed ? framedTarget : previewPrint,
      actions.thresholds.ratioTolerance,
    );
  // Owner 2026-08-01 round 2: NO adjustment echoes under the preview at all —
  // even the staged note forced a scroll. The ✎ pill on the stage shows
  // "Adjusted ✓" instead, costing zero pane height.
  /** A framed print already made for this photo (e.g. framed via ✎
   *  Adjustments) — the pane's pickers never mirror the cart, so say so under
   *  the preview instead of looking like nothing happened. */
  const framedSibling = useMemo(
    () => (previewPhoto ? prints.find((p) => p.photoId === previewPhoto.id && p.frameId) : undefined),
    [prints, previewPhoto],
  );
  /** Stepped mode shows ONE caption line, highest priority first. */
  const mobileNote =
    framedSibling ? `Framed ${sizeIdShort(framedSibling.sizeId)}" print already in your cart` : null;

  const doAdd = () => {
    {
      /* Anchor acceptance: they Added at the auto-anchored size without ever
         touching the picker — the anchor sold the size. Count once, then
         mark touched so repeat Adds are their own choice. */
      const st = useStore.getState();
      if (
        st.anchorSizeId &&
        !st.sizeTouched &&
        applyBar.sizeId === st.anchorSizeId &&
        ANCHOR_BAND.has(st.anchorSizeId)
      ) {
        reportUpsell('accepted', 'anchor_default', st.anchorSizeId);
        useStore.setState({ sizeTouched: true });
      }
    }
    if (!sizeId || count === 0) return;
    const ids = pendingFrame
      ? // The framed print carries the FRAME'S print size (the server validates
        // frame/size consistency) — the warning above Add spells this out.
        actions.addFramedToSelection(
          pendingFrame.sizeId,
          pendingFrame.paper, // the FRAME'S paper — cross-paper picks must not carry the pane's
          applyBar.qty,
          pendingFrame.frameId,
        )
      : actions.addToSelection(sizeId, paper, applyBar.qty, border);
    if (ids.length === 0) return;
    // Apply any STAGED pre-Add adjustments (✎ before Add) to the new prints,
    // then clear them for those photos. Crops apply only at the shape they
    // were made for; rotation/B&W always carry.
    const st = useStore.getState();
    for (const id of ids) {
      const p = st.prints.find((pr) => pr.id === id);
      const adj = p ? st.stagedAdjust[p.photoId] : undefined;
      if (!p || !adj) continue;
      actions.saveCropEdit(id, {
        sizeId: p.sizeId,
        fitMode: adj.fitMode,
        cropRect: adj.fitMode === 'crop' && adj.sizeId === p.sizeId ? adj.cropRect : null,
        qty: p.qty,
        paper: p.paper,
        rotation: asRotation(adj.rotation),
        bw: adj.bw,
        bwStyle: adj.bw ? adj.bwStyle ?? null : null,
        bwStrength: adj.bw ? adj.bwStrength ?? null : null,
        ...(adj.borderMode !== undefined
          ? { borderMode: adj.borderMode, borderMm: adj.borderMm ?? null }
          : {}),
        frameId: p.frameId ?? null,
      });
    }
    const idSet = new Set(ids);
    const added = useStore.getState().prints.filter((p) => idSet.has(p.id));
    const flagged = flaggedForReview(added).length; // same predicate as the Review modal
    st.clearStagedAdjust(added.map((p) => p.photoId));
    clearSelection();
    // Mobile (owner 2026-07-27): the selection just cleared, so this tab is
    // now empty — bounce back to PHOTOS to pick the next batch. Desktop
    // leaves the tab alone (invisible there; would push a dead Back entry).
    if (isMobile) {
      // The batch is done: hand the next one a clean stepped flow.
      st.setSpCfgStep('size');
      st.setSpPendingFrameId(null);
      st.setSpBorder(null);
      st.setSpTab('photos');
    }
    onAdded({
      count: ids.length,
      flagged,
      framed,
      ...(ids.length === 1 ? { singlePrintId: ids[0] } : {}),
    });
  };

  /* ---------- mirror the primary action into the sticky bar (round 5) ----------
     Round 2026-07-27: on mobile this drives the bottom bar's ONE button, which
     is the step CTA ("Next →" / gold "Add to cart · N prints"). Desktop keeps
     its own in-pane Add row and ignores this. */
  /** How many prints Add will create (owner 2026-07-27: badge the button). */
  const addCount = count * applyBar.qty;
  /* LIVE price on the Add button (owner 2026-08-16): the trigger copy of the
     size dropdown no longer carries the price, so the running total for what
     Add will actually create (unit × qty × photos) lives here and updates
     with every size/paper/frame change. Mobile only sees this on the LAST
     step — earlier steps stay "Next →" (cfgCtaLabel). */
  const addTotal = (pendingFrame ? pendingFrame.price : price ?? 0) * addCount;
  const addVerb = pendingFrame ? 'Add framed' : 'Add to cart';
  const addLabel = addTotal > 0 ? `${addVerb} · ${formatMoney(addTotal)}` : addVerb;
  /** Sticky-bar label (string-only) carries the count in words. */
  const addMetaLabel =
    addCount > 1 && addTotal > 0
      ? `${pendingFrame ? `Add ${addCount} framed` : `Add ${addCount} prints`} · ${formatMoney(addTotal)}`
      : addLabel;

  /** Advance a step, or Add on the last one. */
  const doPrimary = () => {
    // Same rule as the desktop Add: a missing size guides instead of dying.
    if (needsSize) {
      nudgeSize();
      return;
    }
    if (!stepped || isLastStep) {
      doAdd();
      return;
    }
    const next = cfgNext(steps, step);
    if (next) setCfgStep(next);
  };
  const primaryLabel = needsSize
    ? 'Choose a size first'
    : stepped
      ? cfgCtaLabel({
          step,
          isLast: isLastStep,
          border,
          addLabel: addMetaLabel,
          total: addTotal > 0 ? formatMoney(addTotal) : undefined,
        })
      : addMetaLabel;
  // Enabled when size is the only blocker, so the tap can carry them to it.
  const primaryReady = needsSize
    ? true
    : stepped
      ? cfgCanAdvance({ step, isLast: isLastStep, hasSize: !!sizeId, border, canAdd })
      : canAdd;

  const primaryRef = useRef(doPrimary);
  primaryRef.current = doPrimary;
  useEffect(() => {
    if (addRef) addRef.current = () => primaryRef.current();
    return () => {
      if (addRef) addRef.current = null;
    };
  }, [addRef]);
  // Primitive deps only — `run` lives in the ref above, so this can't loop.
  useEffect(() => {
    onAddMeta?.({ canAdd: primaryReady, label: primaryLabel, gold: !stepped || isLastStep });
  }, [onAddMeta, primaryReady, primaryLabel, stepped, step]);
  useEffect(() => () => onAddMeta?.(null), [onAddMeta]);

  /** ✎ on a config NOT yet in the order: build a DRAFT print for the crop
   *  dialog. Save STAGES the adjustments (owner 2026-07-27: it must not add
   *  to the cart) — they apply when Add creates the prints. */
  const openDraft = () => {
    if (!previewPhoto) return;
    const isFramedDraft = framed && !!pendingFrame;
    const dSizeId = isFramedDraft ? pendingFrame!.sizeId : sizeId;
    if (!dSizeId) return;
    const dPaper = isFramedDraft ? pendingFrame!.paper : paper;
    const dEntry = catalog.get(dSizeId);
    if (!dEntry) return;
    const staged = stagedAdjust[previewPhoto.id];
    const fields = buildPrintFields(
      previewPhoto,
      dSizeId,
      dPaper,
      1,
      isFramedDraft ? 'none' : border,
      dEntry,
      pricing,
      actions.thresholds,
    );
    setDraft({
      photo: previewPhoto,
      print: {
        id: `draft-${previewPhoto.id}`,
        ...fields,
        ...(isFramedDraft
          ? {
              unitPrice: pendingFrame!.price,
              frameId: pendingFrame!.frameId,
              borderMode: 'none' as const,
              borderDecided: true,
            }
          : {}),
        // Re-open with previously staged tweaks (crop only if same shape).
        ...(staged
          ? staged.sizeId === dSizeId
            ? {
                fitMode: staged.fitMode,
                cropRect: staged.cropRect,
                rotation: asRotation(staged.rotation),
                bw: staged.bw,
              }
            : { rotation: asRotation(staged.rotation), bw: staged.bw }
          : {}),
      },
    });
  };

  const doCrop = () => {
    if (!previewPhoto) return;
    if (framed && pendingFrame) {
      // This exact framed print already in the order → edit it directly. Same
      // lookup the mismatch warning resolves against, so they cannot diverge.
      if (framedTarget) {
        onOpenCrop(framedTarget.id);
        return;
      }
      openDraft();
      return;
    }
    if (!sizeId) return;
    if (previewPrint) {
      onOpenCrop(previewPrint.id); // already in the order → edit the real print
      return;
    }
    openDraft();
  };


  /** Swap the picked frame to the same config in another colour. Keeps the
   *  customer's configuration; only the moulding changes. */
  const pickColour = (next: string) => {
    setFrameColour(next);
    if (!pendingFrameId) return;
    const swapped = frameInColour(pricing, pendingFrameId, next);
    // Null = that colour does not stock this config. Leave the pick alone
    // rather than guessing at a different frame.
    if (swapped) setPendingFrameId(swapped.frameId);
  };

  const pickFrame = (key: string) => {
    if (key === NO_FRAME) {
      setPendingFrameId(null);
      return;
    }
    const option = frameOptions.find((o) => o.frameId === key);
    if (!option) return;
    // Re-clicking the ALREADY-picked config must not clobber a paper sibling
    // chosen in the frame-paper picker (the row lists the config's winner).
    if (
      pendingFrame &&
      option.printSize === pendingFrame.printSize &&
      option.frameSize === pendingFrame.frameSize &&
      option.borderLabel === pendingFrame.borderLabel
    ) {
      return;
    }
    // Any frame is pickable (upsell) — a print-size mismatch warns near Add
    // instead of interrupting with a confirm dialog.
    setPendingFrameId(option.frameId);
  };

  /** The Frame dropdown highlights the picked CONFIG's winner row — a paper
   *  sibling picked below isn't listed by frameId, but its config is. */
  const frameDropValue = pendingFrame
    ? frameOptions.find(
        (o) =>
          o.printSize === pendingFrame.printSize &&
          o.frameSize === pendingFrame.frameSize &&
          o.borderLabel === pendingFrame.borderLabel &&
          o.colour === pendingFrame.colour,
      )?.frameId ?? pendingFrame.frameId
    : NO_FRAME;

  // No price on the closed trigger (owner 2026-08-16) — the live total sits
  // on the Add button; prices live INSIDE the opened list.
  const sizeButtonLabel = entry ? entry.label : 'Choose a size';

  /** Mobile progress bar under the preview — one segment per step. */
  const segments = useMemo(
    () =>
      cfgProgress({
        steps,
        current: step,
        fineArt: isFineArt,
        // A frame sets the print size, so it IS the size step's answer.
        sizeLabel: pendingFrame
          ? `${sizeWithUnit(pendingFrame.frameSize)} ${pendingFrame.colourLabel.toLowerCase()} frame`
          : entry
            ? `${sizeWithUnit(entry.size)}`
            : 'Not chosen',
        paperLabel: pendingFrame
          ? framePaperLabel(pricing, pendingFrame.paper)
          : shortPaperLabel(pricing, paper),
        borderLabel:
          border === null ? 'Not chosen' : border === 'none' ? 'To edge' : borderModeLabel(border),
      }),
    [steps, step, isFineArt, pendingFrame, entry, pricing, paper, border],
  );

  /* Frame rows are DELTA-priced against what they'd pay now (owner 2026-08-16):
     the pending frame if one is picked, else the loose print. Selected row =
     its own muted absolute; cheaper frame = muted absolute; "No frame" = no price at all
     (dropping the frame is an exit, not a product to merchandise — the loose
     price is already on the Add button the moment they pick it). */
  const frameGroups = useMemo(() => {
    const baseline = pendingFrame ? pendingFrame.price : price;
    return [
      {
        label: 'Stocked frames',
        options: [
          { key: NO_FRAME, label: 'No frame (print only)' },
          ...frameOptions.map((o) => {
            const d =
              o.frameId === pendingFrame?.frameId
                ? { text: formatMoney(o.price), muted: true }
                : priceDeltaLabel(o.price, baseline);
            return {
              key: o.frameId,
              label: o.label,
              ...(d ? { right: d.text, ...(d.muted ? { rightMuted: true } : {}) } : {}),
            };
          }),
        ],
      },
    ];
  }, [frameOptions, pendingFrame, price]);

  /* Quick-back mitigation: "ⓘ Paper Details" sits INLINE next to the Customise
     title (was buried on the Paper field's label row) so customers compare
     papers without leaving the page. Same store-held dialog as before. */
  const paperDetailsChip = (
    <button
      type="button"
      className="paper-chip cfg__head-paper"
      onClick={() => setPaperInfoOpen(true)}
    >
      ⓘ Paper Details
    </button>
  );

  /** Owner 2026-07-27: one tap back to the baseline (no size, default finish,
   *  qty 1, no frame, default border, staged adjustments dropped).
   *  Selections/prints already in the order are untouched. */
  const resetDefaults = () => {
    setApplyBar({ sizeId: null, paper: defaultCtPaper(pricing), qty: 1 });
    setPendingFrameId(null);
    setBorder('none');
    setCfgStep('size'); // start the stepped mobile flow over too
    const s = useStore.getState();
    s.clearStagedAdjust(Object.keys(s.stagedAdjust));
  };
  const resetChip = (
    /* Icon-only (owner 2026-07-27) with an INSTANT tooltip — saves header
       room; Paper Details keeps its wording per the owner. */
    <button
      type="button"
      className="paper-chip cfg__head-reset"
      data-tip="Reset size, paper, frame and quantity"
      aria-label="Reset size, paper, frame and quantity to the defaults"
      onClick={resetDefaults}
    >
      ↺
    </button>
  );

  /* mobile-only (CSS-gated): with the STEP N OF 4 header row gone on phones,
     the pane title carries the way back to Upload */
  const backToUpload = (
    <button
      type="button"
      className="pane__back"
      onClick={() => setStep(0)}
      aria-label="Back to upload"
    >
      ‹
    </button>
  );

  /* ---------- placeholder when nothing is selected ---------- */
  if (count === 0) {
    return (
      <section className="pane pane--configure">
        <header className="pane__head">
          {backToUpload}
          <h2 className="pane__title">Customise</h2>
          {paperDetailsChip}
          <span className="pane__sub">Nothing selected</span>
        </header>
        <div className="pane__empty cfg__placeholder">
          <span className="cfg__placeholder-arrow" aria-hidden>
            ↑
          </span>
          <p>Pick a photo to start.</p>
          <span className="cfg__placeholder-hint">Tap any photo to choose its size, paper &amp; crop. Select several to size them together.</span>
        </div>
      </section>
    );
  }

  // "Framing changes your print to …" — it must read next to the frame picker
  // itself, so it renders in the Frame field on mobile and in the Add row on
  // desktop (same node, one place it can appear at a time).
  const frameWarn =
    pendingFrame && (frameSizeChanges || framePaperChanges) ? (
      <span className="cfg__frame-warn">
        {frameSizeChanges && framePaperChanges ? (
          <>
            Framing changes your print to a {sizeWithUnit(pendingFrame.printSize)}{' '}
            {framePaperLabel(pricing, pendingFrame.paper)} print to fit this frame.
          </>
        ) : frameSizeChanges ? (
          <>
            Framing changes the print size to {sizeWithUnit(pendingFrame.printSize)} to fit this
            frame.
          </>
        ) : (
          <>
            Framing prints this on {framePaperLabel(pricing, pendingFrame.paper)} to suit this
            frame.
          </>
        )}
      </span>
    ) : null;

  return (
    <section
      className={`pane pane--configure${stepped ? ' cfg--stepped' : ''}`}
      data-cfgdir={stepped ? stepDir : undefined}
      /* The last step is the tall one: it adds the qty row AND, for C-Type,
         the border control on top of the paper picker. The preview gives that
         height back rather than letting the step scroll (owner 2026-07-27). */
      data-cfglast={stepped && isLastStep ? '1' : undefined}
      /* A picked frame adds the colour swatch row (26px + gap) to the Size
         step. That row did not exist when the stage budget below was derived,
         so without this the step scrolls the moment a frame is chosen. */
      data-cfgframe={stepped && pendingFrame ? '1' : undefined}
    >
      <header className="pane__head">
        {backToUpload}
        <h2 className="pane__title">Customise</h2>
        {/* Stepped mobile owns this control: it sits under the paper picker on
            the Paper step, so the header copy would be a second identical
            button on the same screen. */}
        {!stepped && paperDetailsChip}
        {resetChip}
        <span className="pane__sub">{`Applies to ${count} photo${count === 1 ? '' : 's'}`}</span>
      </header>

      <div className="cfg__scroll">
        {previewPhoto && entry && (
          <div className="cfg__preview">
            <div
              className={`cfg__preview-stage${framed && framedMock ? ' cfg__preview-stage--framed' : ''}`}
              tabIndex={0}
              role="group"
              aria-label="Print preview. Use the arrow keys to flip through your selection."
              onKeyDown={(e) => {
                if (e.key === 'ArrowLeft') stepPreview(-1);
                else if (e.key === 'ArrowRight') stepPreview(1);
              }}
            >
              {framed && framedMock ? (
                stagedA ? (
                  // Staged pre-Add adjustments render live inside the frame.
                  <FramePreviewModes
                    photo={previewPhoto}
                    option={framedMock}
                    cropRect={
                      stagedA.fitMode === 'crop' && stagedA.sizeId === pendingFrame?.sizeId
                        ? stagedA.cropRect
                        : null
                    }
                    rotation={asRotation(stagedA.rotation)}
                    bw={stagedA.bw}
                    bwStyle={stagedA.bwStyle ?? null}
                    bwStrength={stagedA.bwStrength ?? null}
                    fit={stagedA.fitMode === 'fit'}
                  />
                ) : (
                  <FramePreviewModes photo={previewPhoto} option={framedMock} />
                )
              ) : (
                <PrintPreview
                  photo={previewPhoto}
                  sizeId={sizeId!}
                  inches={entry.inches}
                  fitMode={stagedA ? stagedA.fitMode : previewPrint?.fitMode ?? 'crop'}
                  cropRect={
                    stagedA
                      ? // No rotation prop on PrintPreview — a rotated crop
                        // can't render truthfully (same rule as previewPrint).
                        !asRotation(stagedA.rotation) && stagedA.sizeId === sizeId
                        ? stagedA.cropRect
                        : null
                      : previewPrint && !(previewPrint.rotation ?? 0)
                        ? previewPrint.cropRect
                        : null
                  }
                  borderMode={framed ? 'none' : border ?? 'none'}
                  borderMm={
                    framed ? null : stagedA?.borderMm ?? previewPrint?.borderMm ?? null
                  }
                  bw={stagedA ? stagedA.bw : previewPrint?.bw ?? false}
                  bwStyle={stagedA ? stagedA.bwStyle ?? null : previewPrint?.bwStyle ?? null}
                  bwStrength={stagedA ? stagedA.bwStrength ?? null : previewPrint?.bwStrength ?? null}
                  pricing={pricing}
                  className="cfg__preview-img"
                />
              )}
              {count > 1 && (
                <>
                  <button
                    type="button"
                    className="cfg__preview-nav cfg__preview-nav--prev"
                    aria-label="Previous selected photo"
                    onClick={() => stepPreview(-1)}
                  >
                    ‹
                  </button>
                  <button
                    type="button"
                    className="cfg__preview-nav cfg__preview-nav--next"
                    aria-label="Next selected photo"
                    onClick={() => stepPreview(1)}
                  >
                    ›
                  </button>
                </>
              )}
              {/* Owner 2026-07-27: the mismatch warning OVERLAYS the preview
                  (below the stage it stretched the pane on some screens) and
                  is itself the shortcut into Adjustments. */}
              {/* Owner 2026-07-27: three lines of prose read as a notice, not a
                  decision. One short line and ONE button. No "auto crop"
                  action: that is already the default if they do nothing, so
                  the button would have changed nothing they could see. */}
              {previewCrops && (
                <div className="cfg__owarn">
                  <span className="cfg__owarn-msg">Image doesn&rsquo;t fit print</span>
                  <button type="button" className="cfg__owarn-btn" onClick={doCrop}>
                    Fix
                  </button>
                </div>
              )}
              {/* Owner 2026-07-27: ✎ was a 21px chip in the summary row and read
                  as decoration. As a pill ON the preview it is a real 34px
                  target, sits on the thing it edits, and costs no pane height.
                  Hidden while the mismatch warning owns the same corner. */}
              {canCrop && !previewCrops && (
                <button type="button" className="cfg__adjust" onClick={doCrop}>
                  {stagedA ? '✎ Adjusted ✓' : '✎ Adjust'}
                </button>
              )}
              {/* Owner 2026-07-29: on mobile the "1 of N" counter rides the
                  stage as a pill, like ✎ Adjust does, instead of taking a
                  caption line. It was the ONE thing escaping the one-line rule
                  below, so multi-select quietly cost 25px that the stage budget
                  had never been told about, and with a frame selected the last
                  line scrolled out of view. The filename goes with it: the
                  photo is right there, and the Photos tab has the names. */}
              {stepped && count > 1 && (
                <span className="cfg__preview-count">{`${idx + 1} / ${count}`}</span>
              )}
            </div>
            {!stepped && count > 1 && (
              <span className="cfg__preview-cap">{`${idx + 1} of ${count} · ${previewPhoto.filename}`}</span>
            )}
            {/* Adjustment echoes fully removed (owner 2026-08-01): the ✎ pill
                on the stage is both the affordance and the staged indicator. */}
            {!stepped && framedSibling && (
              <span className="cfg__preview-wb">
                This photo has a framed {sizeIdShort(framedSibling.sizeId)}&quot; print · manage it
                in your cart
              </span>
            )}
            {stepped && mobileNote && <span className="cfg__preview-wb">{mobileNote}</span>}
          </div>
        )}

        {/* PROGRESS, not navigation (owner 2026-07-27). The old row of chips was
            all-tappable and customers hopped around with it instead of working
            the flow, landing on steps whose earlier choices were still unmade.
            Now a step is tappable only once it is BEHIND you; forward movement
            belongs to the CTA. This row also replaces the separate
            "Step 1 of 2 · Size & frame" header, which said the same thing. */}
        {stepped && (
          <div className="cfgprog" role="group" aria-label="Your progress">
            {stepIdx > 0 ? (
              <button
                type="button"
                className="cfgprog__back"
                aria-label="Previous step"
                onClick={() => {
                  const prev = cfgPrev(steps, step);
                  if (prev) setCfgStep(prev);
                }}
              >
                ‹
              </button>
            ) : (
              <span className="cfgprog__back cfgprog__back--ghost" aria-hidden />
            )}
            {segments.map((seg, i) => {
              const body = (
                <>
                  <span className="cfgprog__track" aria-hidden />
                  <span className="cfgprog__title">
                    {i + 1}. {seg.title}
                  </span>
                  <span className="cfgprog__value">{seg.value}</span>
                </>
              );
              return seg.canJump ? (
                <button
                  key={seg.step}
                  type="button"
                  className={`cfgprog__seg cfgprog__seg--${seg.state}`}
                  onClick={() => setCfgStep(seg.step)}
                  aria-label={`Back to ${seg.title}: ${seg.value}`}
                >
                  {body}
                </button>
              ) : (
                <span
                  key={seg.step}
                  className={`cfgprog__seg cfgprog__seg--${seg.state}`}
                  aria-current={seg.state === 'current' ? 'step' : undefined}
                >
                  {body}
                </span>
              );
            })}
          </div>
        )}

        {/* Irrelevant while framed — the frame dictates the print size. */}


        {stepOn('size') && (
        <div
          className={`cfg__field${sizeNudge ? ' cfg__field--nudge' : ''}`}
          ref={sizeFieldRef}
        >
          <div className="eyebrow cfg__label">Size</div>
          {framed && pendingFrame ? (
            /* Owner 2026-07-27: a picked frame LOCKS the size — showing the
               loose size dropdown next to a 16×24" frame read as a bug. */
            <div className="cfg__border-locked">
              {sizeWithUnit(pendingFrame.printSize)} print · set by the{' '}
              {sizeWithUnit(pendingFrame.frameSize)} frame
            </div>
          ) : (
            <div className="cfg__sizerow">
            <Dropdown
              variant="light"
              className="cfg__sizedd"
              ariaLabel="Print size"
              groups={sizeGroups}
              value={sizeId}
              buttonLabel={sizeButtonLabel}
              highlightKey={frozen?.sizeId ?? largestRow?.sizeId ?? null}
              highlightBadge={frozen?.badge ?? (largestRow ? 'Largest for this photo' : undefined)}
              openToken={sizeOpenToken}
              onChange={(key) => {
                if (key === CUSTOM_SIZE_OPTION_KEY) {
                  setCustomOpen(true);
                  return;
                }
                if (frozen && key === frozen.sizeId) {
                  reportUpsell('accepted', 'size_largest', key);
                  setUpsellTaken((n) => n + 1);
                }
                // Any pick after opening the suggestion means they have had
                // their answer — the button now switches to selling fine art.
                setUpsellSeen(true);
                setFrozen(null);
                setApplyBar({ sizeId: key, paper: paperForSize(pricing, key, paper) });
              }}
            />
            {upsell && (
              <button
                type="button"
                className="cfg__upsell"
                onClick={() => {
                  if (upsell.kind === 'largest') {
                    // "How big can I go?" — SHOW them, in context, highlighted.
                    setFrozen({ sizeId: upsell.entry.sizeId, badge: upsell.badge });
                    setSizeOpenToken((t) => t + 1);
                    return;
                  }
                  /* A priced upsell ("Fine art 8×10" · +$13.15") is a decision
                     already made — reopening the list to make them find the
                     row again is friction, so apply it. */
                  reportUpsell('accepted', `size_${upsell.kind}`, upsell.entry.sizeId);
                  setUpsellTaken((n) => n + 1);
                  setUpsellSeen(true);
                  setApplyBar({
                    sizeId: upsell.entry.sizeId,
                    paper: paperForSize(pricing, upsell.entry.sizeId, paper),
                  });
                }}
              >
                <span className="cfg__upsell-label">{upsell.label}</span>
                {upsell.price && <span className="cfg__upsell-price">{upsell.price.text}</span>}
              </button>
            )}
            </div>
          )}
        </div>
        )}

        {stepOn('paper') && (
        <div className="cfg__field">
          <div className="eyebrow cfg__label">{framed || isFineArt ? 'Paper' : 'Finish'}</div>
          {framed && pendingFrame ? (
            /* The papers THIS frame config comes in — picking one switches the
               framed product (price updates; the size stays the frame's). */
            <Dropdown
              variant="light"
              ariaLabel="Paper for this frame"
              groups={[
                {
                  label: 'Papers for this frame',
                  options: frameSiblings.map((f) => {
                    // Same delta rule as everywhere: current = no label,
                    // dearer = "+$X", cheaper = muted absolute.
                    const d =
                      f.frameId === pendingFrame.frameId
                        ? { text: formatMoney(f.price), muted: true }
                        : priceDeltaLabel(f.price, pendingFrame.price);
                    return {
                      key: f.frameId,
                      label: framePaperLabel(pricing, f.paper),
                      ...(d ? { right: d.text, ...(d.muted ? { rightMuted: true } : {}) } : {}),
                    };
                  }),
                },
              ]}
              value={pendingFrame.frameId}
              buttonLabel={framePaperLabel(pricing, pendingFrame.paper)}
              onChange={(key) => setPendingFrameId(key)}
            />
          ) : isFineArt && sizeId ? (
            <div className="cfg__sizerow">
              <Dropdown
                variant="light"
                className={paperUpsell ? 'cfg__sizedd' : undefined}
                ariaLabel="Fine art paper"
                groups={paperGroups}
                value={paper}
                buttonLabel={shortPaperLabel(pricing, paper)}
                onChange={(key) => setApplyBar({ paper: key as PaperId })}
              />
              {paperUpsell && (
                <button
                  type="button"
                  className="cfg__upsell"
                  onClick={() => {
                    /* Delta shown = decision made (same rule as the size
                       button's fine-art mode): one press applies the paper. */
                    reportUpsell('accepted', 'paper_premium', paperUpsell.paper);
                    setPaperUpTaken(true);
                    setApplyBar({ paper: paperUpsell.paper });
                  }}
                >
                  <span className="cfg__upsell-label">{paperUpsell.label}</span>
                  <span className="cfg__upsell-price">{paperUpsell.price.text}</span>
                </button>
              )}
            </div>
          ) : (
            <Segmented
              variant="light"
              className="cfg__finish"
              ariaLabel="Finish"
              segments={ctPapers.map((p) => ({
                key: p,
                label: pricing.papers[p]?.label ?? p,
                // Per-size restriction: 8×12 offers Lustre only, so Gloss greys out.
                disabled: sizeId ? !isPaperValidForSize(pricing, sizeId, p) : false,
              }))}
              value={isCtPaper(pricing, paper) ? paper : defaultCtPaper(pricing)}
              onChange={(key) => setApplyBar({ paper: key as PaperId })}
            />
          )}
          {/* FINE ART TIER (#5): the third chip, shown only when fine art stocks
              these exact dimensions. Delta-priced ("from +$4.90"), never an
              absolute. Tapping switches the print to the fine art size on its
              cheapest paper; the paper dropdown then offers the rest. */}
          {!framed && !isFineArt && fineArtUpgrade && (
            <button
              type="button"
              className="cfg__fatier"
              onClick={() => {
                reportUpsell('accepted', 'paper_fineart', fineArtUpgrade.faSizeId);
                setApplyBar({
                  sizeId: fineArtUpgrade.faSizeId,
                  paper: paperForSize(pricing, fineArtUpgrade.faSizeId, paper),
                });
              }}
            >
              <span className="cfg__fatier-name">Fine Art</span>
              <span className="cfg__fatier-price">from {fineArtUpgrade.delta.text}</span>
            </button>
          )}
          {!framed && restrictionNote && <span className="paper-note">{restrictionNote}</span>}
          {/* Compare papers right where you choose them (owner 2026-07-27). */}
          {stepped && (
            <button
              type="button"
              className="paper-chip cfg__paperinfo"
              onClick={() => setPaperInfoOpen(true)}
            >
              ⓘ Paper Details
            </button>
          )}
          {/* C-Type's border is a simple binary, so it rides HERE on mobile
              (owner 2026-07-27); fine art earns its own step below. */}
          {stepped && sizeId && !framed && !isFineArt && (
            <>
              <BorderControl pricing={pricing} sizeId={sizeId} framed={false} value={border} onChange={setBorder} />
              {wbCaption && <span className="cfg__preview-wb">{wbCaption}</span>}
            </>
          )}
        </div>
        )}

        {/* Frame sits WITH the size (owner 2026-07-27 round 2): picking one
            rewrites the print size anyway, so they belong on one screen. */}
        {stepOn('size') && frameOptions.length > 0 && (
          /* --wide: frame labels are long — full row in the two-up desktop grid */
          <div className="cfg__field cfg__field--wide">
            <div className="eyebrow cfg__label">Frame</div>
            <Dropdown
              variant="light"
              ariaLabel="Frame"
              groups={frameGroups}
              value={frameDropValue}
              buttonLabel={pendingFrame ? pendingFrame.label : 'No frame (print only)'}
              onChange={pickFrame}
            />
            {/* Colour rides WITH the frame, inline (owner 2026-07-28): it costs
                no step on mobile and keeps the option labels short, where
                doubling the dropdown would not. Gated on pendingFrame (round 2):
                showing a colour picker above "No frame (print only)" reads as a
                decision you must make before moving on, and confused customers
                who did not want a frame at all. */}
            {pendingFrame && (
              <FrameColourPicker
                colours={colours}
                value={frameColour}
                onChange={pickColour}
              />
            )}
            {/* Mobile drops the blurb: the framed Add step was overflowing,
                which scrolled the summary chips out of view. */}
            {framed && !stepped && (
              <span className="cfg__frame-hint">
                {pendingFrame?.colourLabel ?? 'Black'} timber frame · real glass · print
                included. Ready to hang.
              </span>
            )}
            {stepped && frameWarn}
          </div>
        )}

        {/* Desktop only: on mobile the frame chip already says it, and the
            extra field pushed the step past the viewport. */}
        {!stepped && framed && pendingFrame && (
          <div className="cfg__field">
            <div className="eyebrow cfg__label">Border</div>
            <div className="cfg__border-locked">{pendingFrame.borderLabel} (set by this frame)</div>
          </div>
        )}
        {/* Desktop: always. Mobile: fine art gets its own Border step (C-Type's
            rides on the Paper step above). */}
        {(stepped ? step === 'border' : true) && sizeId && !framed && (
          <>
            <BorderControl pricing={pricing} sizeId={sizeId} framed={false} value={border} onChange={setBorder} />
            {wbCaption && <span className="cfg__preview-wb">{wbCaption}</span>}
          </>
        )}

        {/* Owner 2026-08-01: the full-width ✎ Adjustments button is gone — the
            ✎ Adjust pill on the preview (now desktop too) is the affordance,
            and it doubles as the staged-state indicator ("✎ Adjusted ✓"). */}
        {/* Print Check chips removed (owner 2026-07-27): clicking them only
            filtered the grid without resolving anything — the Add toast and
            the Continue review dialogs carry the warnings instead. */}
      </div>

      {/* Mobile: only the LAST step needs this row (qty beside the CTA), and
          its gold button lives in the bottom bar — that's the 67px the
          no-scroll fit buys. */}
      {(!stepped || isLastStep) && (
      <div className="cfg__actions">
        {!stepped && frameWarn}
        {/* Quantity lives beside Add (owner 2026-07-27) — one action row. */}
        <div className="cfg__actions-qty">
          <span className="eyebrow cfg__label">Qty</span>
          <QtyStepper
            variant="light"
            ariaLabel="Quantity per photo"
            value={applyBar.qty}
            onChange={(qty) => setApplyBar({ qty })}
          />
        </div>
        {!stepped && (
          // Never `disabled` on a missing size — see needsSize above. It stays
          // live, relabels, and the click opens the size picker.
          <button
            type="button"
            className="btn btn--gold cfg__add"
            onClick={needsSize ? nudgeSize : doAdd}
          >
            {needsSize ? 'Choose a size first' : addLabel}
            {!needsSize && addCount > 1 && (
              <span className="cfg__add-count" aria-label={`${addCount} prints`}>
                {addCount}
              </span>
            )}
          </button>
        )}
      </div>
      )}

      {draft && (
        // STAGED pre-Add adjustments: Save hands the crop/rotation/B&W back
        // to the pane (nothing joins the order until Add).
        <CropEditor
          key={draft.print.id}
          print={draft.print}
          photo={draft.photo}
          pricing={pricing}
          catalog={catalog}
          actions={actions}
          onApply={(edit) => {
            setStagedAdjust(draft.photo.id, {
              sizeId: draft.print.sizeId,
              fitMode: edit.fitMode,
              cropRect: edit.cropRect,
              rotation: edit.rotation,
              bw: edit.bw,
              bwStyle: edit.bw ? edit.bwStyle ?? null : null,
              bwStrength: edit.bw ? edit.bwStrength ?? null : null,
              ...(edit.borderMode !== undefined
                ? { borderMode: edit.borderMode, borderMm: edit.borderMm ?? null }
                : {}),
            });
            // Keep the pane's border selector in step with the staged choice
            // (same 2026-08-01 sync bug as the saved-print path).
            if (edit.borderMode !== undefined) setBorder(edit.borderMode);
            onToast?.('Adjustments saved · applied when you add ✓');
          }}
          onClose={() => setDraft(null)}
        />
      )}

      {customOpen && (
        <CustomSizeDialog
          pricing={pricing}
          initialPaper={paper}
          photo={previewPhoto ?? null}
          onCreate={(id, customPaper) => {
            addCustomSize(id); // session recents → the dropdown lists it for reuse
            setApplyBar({ sizeId: id, paper: customPaper });
          }}
          onClose={() => setCustomOpen(false)}
        />
      )}

    </section>
  );
}
